import cx from "classnames";
import {
  CLICKED_DELETE_DOCUMENT_ON_RECORDS_AND_SETUP,
  CLICKED_REPLACE_DOCUMENT_ON_RECORDS_AND_SETUP,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useParams } from "react-router-dom";
import {
  RecentDocument,
  useGetEntityOverviewQuery,
} from "v2/store/entityDocumentsApi";
import { groupRecentBySection } from "./groupRecentBySection";
import { ScrollListHint } from "./ScrollListHint";
import { UnmatchedFilesBanner } from "./UnmatchedFilesBanner";
import { UploadedDocumentRow } from "./UploadedDocumentRow";
import { useScrollListHint } from "./useScrollListHint";

type RecentlyUploadedByYouProps = {
  documents: RecentDocument[];
};

export const RecentlyUploadedByYou = ({
  documents,
}: RecentlyUploadedByYouProps) => {
  const { entityId = "" } = useParams<{ entityId: string }>();
  const { trackEvent } = useAnalytics();
  const { ref, showHint, onScroll, scrollDown } =
    useScrollListHint<HTMLDivElement>(documents.length);

  const { data: overview } = useGetEntityOverviewQuery({ entityId });
  const hasUnmatched = (overview?.unmatchedCount ?? 0) > 0;

  const handleReplaceClick = (document: RecentDocument): void =>
    trackEvent(CLICKED_REPLACE_DOCUMENT_ON_RECORDS_AND_SETUP, {
      document_name: document.name,
      section: document.section,
    });

  const handleDeleteClick = (document: RecentDocument): void =>
    trackEvent(CLICKED_DELETE_DOCUMENT_ON_RECORDS_AND_SETUP, {
      document_name: document.name,
      section: document.section,
    });

  if (documents.length === 0 && !hasUnmatched) return null;

  const groups = groupRecentBySection(documents);

  return (
    <div className="t-rounded-2xl t-border t-border-solid t-border-neutral-10 t-bg-surface t-px-8 t-pt-8">
      <div className="t-mb-1 t-flex t-items-center t-gap-1.5 t-text-subtitle-lg t-text-text-60">
        Recently uploaded by you
      </div>
      <p className="t-mb-4 t-text-body t-text-neutral-50">
        Recent documents and records uploaded by you in the last 30 days.
      </p>

      <UnmatchedFilesBanner />

      <div
        className={cx("t-relative t-mb-3", {
          "t-hidden": documents.length === 0,
        })}
      >
        <div
          ref={ref}
          onScroll={onScroll}
          className="hide-scrollbar t-max-h-[420px] t-overflow-y-auto t-pb-3 t-pr-1.5"
        >
          {groups.map((group) => (
            <div
              key={group.section ?? "unclassified"}
              className="t-mb-3 last:t-mb-0"
            >
              {group.section && (
                <div className="t-mb-1 t-px-2 t-text-body-sm t-text-text-30">
                  {group.section}
                </div>
              )}
              {group.documents.map((document) => (
                <UploadedDocumentRow
                  key={document.id}
                  document={document}
                  onReplaceClick={handleReplaceClick}
                  onDeleteClick={handleDeleteClick}
                />
              ))}
            </div>
          ))}
        </div>

        {showHint && <ScrollListHint onClick={scrollDown} />}
      </div>
    </div>
  );
};
